import { supabase } from './supabase.js';
import { coerceUrl } from './url.js';

const SELECT_COLUMNS = [
  'id',
  'title',
  'type',
  'description',
  'url',
  'date_start',
  'date_end',
  'location',
  'expires_at',
  'source_table',
  'source_id',
  'status',
  'submitted_by',
  'created_at',
  'updated_at',
].join(', ');

export const WHATSNEW_TYPES = [
  'conference',
  'call_for_papers',
  'new_publication',
  'forthcoming',
  'announcement',
  'other',
];

// Types that represent dated events (where upcoming/past distinction matters).
export const EVENT_TYPES = new Set(['conference', 'call_for_papers']);

export const EMPTY_WHATSNEW = {
  title: '',
  type: 'announcement',
  description: '',
  url: '',
  date_start: '',
  date_end: '',
  location: '',
};

export const EMPTY_WHATSNEW_FILTERS = {
  type: '',
};

// Local calendar date as YYYY-MM-DD (expires_at is a DATE column).
function todayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Published notices that haven't expired. Auto-generated notices (migration
// 019) carry expires_at = publication date + 30 days; hand-written ones have
// expires_at = NULL and never drop out.
export async function fetchPublishedWhatsNew() {
  const { data, error } = await supabase
    .from('whats_new')
    .select(SELECT_COLUMNS)
    .eq('status', 'published')
    .or(`expires_at.is.null,expires_at.gte.${todayISO()}`)
    .order('date_start', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function fetchWhatsNew(id) {
  const { data, error } = await supabase
    .from('whats_new')
    .select(SELECT_COLUMNS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

function blank(v) {
  return v == null || v === '' ? null : v;
}

function normalize(form) {
  return {
    title: form.title?.trim(),
    type: form.type,
    description: blank(form.description),
    url: coerceUrl(form.url),
    date_start: blank(form.date_start),
    date_end: blank(form.date_end),
    location: blank(form.location?.trim?.()),
  };
}

export function validateWhatsNew(form) {
  if (!form.title?.trim()) return 'whatsNew.form.errors.titleRequired';
  if (!WHATSNEW_TYPES.includes(form.type)) return 'whatsNew.form.errors.typeRequired';
  if (form.date_start && form.date_end && form.date_end < form.date_start) {
    return 'whatsNew.form.errors.dateRangeInvalid';
  }
  return null;
}

export async function createWhatsNew(form, { authorId, asAdmin }) {
  const row = {
    ...normalize(form),
    status: asAdmin ? 'published' : 'pending',
    submitted_by: authorId,
  };
  const { data, error } = await supabase
    .from('whats_new')
    .insert(row)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function updateWhatsNew(id, form) {
  const { data, error } = await supabase
    .from('whats_new')
    .update(normalize(form))
    .eq('id', id)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteWhatsNew(id) {
  const { error } = await supabase.from('whats_new').delete().eq('id', id);
  if (error) throw error;
}

// Decide whether a record's event window is in the future. Non-event types
// (announcements, publications) are never "upcoming"; they live in a single
// reverse-chronological list.
export function isUpcoming(item, today = new Date()) {
  if (!EVENT_TYPES.has(item.type)) return false;
  const reference = item.date_end || item.date_start;
  if (!reference) return false;
  return new Date(reference) >= startOfDay(today);
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function filterWhatsNew(items, { type, query }) {
  const q = (query ?? '').trim().toLowerCase();
  return items.filter((m) => {
    if (type && m.type !== type) return false;
    if (q) {
      const haystack = [m.title, m.description, m.location]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

// Partition results into upcoming events vs everything else, both sorted in
// the appropriate direction (upcoming = soonest first; rest = newest first).
export function partitionWhatsNew(items, today = new Date()) {
  const upcoming = [];
  const past = [];
  for (const m of items) {
    if (isUpcoming(m, today)) upcoming.push(m);
    else past.push(m);
  }
  upcoming.sort((a, b) => (a.date_start ?? '').localeCompare(b.date_start ?? ''));
  // `past` is already sorted by the SQL fetcher (date_start DESC, created_at DESC).
  return { upcoming, past };
}

// Where a trigger-generated notice came from. Returns the in-site path that
// opens the source row's detail view (the list pages honour ?entry=<id>), or
// null for hand-written notices.
const SOURCE_PATHS = {
  bibliography: '/bibliography',
  organizations: '/organizations',
  teaching_materials: '/teaching',
  research_tools: '/research-tools',
  broader_engagements: '/broader-engagements',
};

export function sourcePath(item) {
  const base = SOURCE_PATHS[item?.source_table];
  if (!base || !item.source_id) return null;
  return `${base}?entry=${encodeURIComponent(item.source_id)}`;
}

export function deriveWhatsNewFacets(items) {
  const types = new Map();
  for (const m of items) {
    if (m.type) types.set(m.type, (types.get(m.type) ?? 0) + 1);
  }
  return {
    types: WHATSNEW_TYPES.map((t) => ({ type: t, count: types.get(t) ?? 0 })),
  };
}

// Format a date range for display. Falls back to start-only when no end is set.
export function formatDateRange(start, end, locale = 'en') {
  if (!start) return null;
  const s = new Date(start);
  const fmt = (d) => d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  if (!end || end === start) return fmt(s);
  const e = new Date(end);
  return `${fmt(s)} – ${fmt(e)}`;
}
